import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { redirectToLogin } from './redirectToLogin.js';
export const AuthContext = createContext({ user: null, loading: true, refresh: () => { } });
export function isPlatformUser(user) {
    return user?.platformRole === 'admin' || user?.platformRole === 'viewer';
}
// A tenant admin for UI gating: platform god-mode (admin/viewer), the owning org's
// admin, or the active tenant's admin. Mirrors the server's requireAdmin — the API
// is the real enforcement; this only gates affordances (defense-in-depth: UX only).
export function isTenantAdmin(user) {
    return isPlatformUser(user) || user?.orgRole === 'admin' || user?.tenantRole === 'admin';
}
// A callcenter agent: holds the `callcenter` product scope but is NOT any kind of
// admin (platform / org / tenant). Mirrors the server's isCallcenterAgent in
// identity's home-resolver — such a session's home is the operator desk, so any
// admin surface (console/workspace) redirects it out. UX-only, like isTenantAdmin.
export function isCallcenterAgent(user) {
    if (!user)
        return false;
    return (!isPlatformUser(user) &&
        user.orgRole !== 'admin' &&
        user.tenantRole !== 'admin' &&
        !!user.productScopes?.['callcenter']);
}
// The callcenter product-scope value carries the operator's CC role (Axis B):
// agent / supervisor / admin. Shown on the desk so an operator reads as
// "Call-center agent", not the generic tenant "Member".
const CC_ROLE_LABEL = {
    agent: 'Call-center agent',
    supervisor: 'Call-center supervisor',
    admin: 'Call-center admin',
};
// The one human role label shown in every shell footer — kept here so the apps
// can't drift (they did: "member" vs "Member" across product shells vs workspace).
// Tenant/org admin win first (they outrank a product scope); a plain member who
// holds the callcenter scope reads by that scope before the generic "Member".
export function roleLabel(user) {
    if (user?.tenantRole === 'admin')
        return 'Tenant admin';
    if (user?.orgRole === 'admin')
        return 'Org admin';
    const cc = user?.productScopes?.['callcenter'];
    if (cc)
        return CC_ROLE_LABEL[cc] ?? 'Call-center agent';
    if (user?.tenantRole === 'user')
        return 'Member';
    if (user?.platformRole)
        return `Platform ${user.platformRole}`;
    return 'Member';
}
// The /api/auth/me contract in one place — every SPA's AuthProvider used to restate
// this path + response shape. Structurally typed on `{ get }` so web-auth needs no
// @retink/web-ui (Api) dependency.
export function fetchMe(api) {
    return api.get('/api/auth/me').then((r) => r.user);
}
export const AUTH_CHECK_TIMEOUT_MS = 15_000;
export function withAuthTimeout(promise, timeoutMs = AUTH_CHECK_TIMEOUT_MS) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('Session check timed out')), timeoutMs);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
export function AuthErrorState({ onRetry, onSignIn }) {
    return (_jsx("div", { className: "flex h-full items-center justify-center bg-slate-950 px-6", children: _jsxs("div", { className: "max-w-sm text-center", role: "alert", children: [_jsx("h1", { className: "text-base font-semibold text-slate-100", children: "Couldn\u2019t confirm your session" }), _jsx("p", { className: "mt-2 text-sm text-slate-400", children: "Check your connection, then retry. You can also sign in again." }), _jsxs("div", { className: "mt-5 flex justify-center gap-3", children: [_jsx("button", { type: "button", onClick: onRetry, className: "rounded-md bg-slate-100 px-3 py-2 text-sm font-medium text-slate-950", children: "Retry" }), _jsx("button", { type: "button", onClick: onSignIn, className: "rounded-md border border-slate-700 px-3 py-2 text-sm font-medium text-slate-200", children: "Sign in again" })] })] }) }));
}
export function AuthProvider({ children, idUrl, fetchMe, timeoutMs = AUTH_CHECK_TIMEOUT_MS, unauthenticated }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const fetchMeRef = useRef(fetchMe);
    const requestRef = useRef(0);
    useEffect(() => { fetchMeRef.current = fetchMe; }, [fetchMe]);
    const refresh = useCallback(() => {
        const request = ++requestRef.current;
        setLoading(true);
        setError(false);
        withAuthTimeout(Promise.resolve().then(() => fetchMeRef.current()), timeoutMs)
            .then((u) => { if (request === requestRef.current)
            setUser(u); })
            .catch(() => { if (request === requestRef.current)
            setError(true); })
            .finally(() => { if (request === requestRef.current)
            setLoading(false); });
    }, [timeoutMs]);
    useEffect(() => {
        refresh();
        return () => { requestRef.current += 1; };
    }, [refresh]);
    useEffect(() => {
        if (!loading && !error && !user && unauthenticated === undefined)
            redirectToLogin(idUrl);
    }, [loading, error, user, idUrl, unauthenticated]);
    if (loading) {
        return (_jsx("div", { className: "flex h-full items-center justify-center bg-slate-950", children: _jsx("div", { className: "text-slate-400 text-sm", children: "Loading\u2026" }) }));
    }
    if (error)
        return _jsx(AuthErrorState, { onRetry: refresh, onSignIn: () => redirectToLogin(idUrl) });
    if (!user) {
        if (unauthenticated !== undefined)
            return unauthenticated;
        return (_jsx("div", { className: "flex h-full items-center justify-center bg-slate-950", children: _jsx("div", { className: "text-slate-400 text-sm", children: "Taking you to sign in\u2026" }) }));
    }
    return (_jsx(AuthContext.Provider, { value: { user, loading, refresh }, children: children }));
}
